/**
 * Parts Custody field surfaces (PARTS-CUSTODY.md items 3–4):
 *   · PartsForJobCard — "Parts for this job" in the job briefing, with the
 *     one-tap [I have it] hand-off (custody → issued, holder = tech).
 *   · PartsDispositionDialog — the required "where's the part?" question
 *     fired by the Can't Complete / Complete gates when issued tracked
 *     parts are still on the job.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Package, Hand, CheckCircle, Loader2 } from "lucide-react";
import { isPostWorkStatus } from "./steps";

export interface TrackedJobPart {
    id: string;
    short_code: string;
    description: string;
    unit_cost: number;
    custody_state: string;
    holder_name: string | null;
    staged_note: string | null;
}

// #829: physical objects custody doesn't follow — PO lines below the tracking
// threshold. Real things that still have to be on the truck.
export interface PullSheetLine {
    po_line_id: string;
    part_name: string;
    quantity: number;
    received_qty: number;
    is_fully_received: boolean;
    po_number: string | null;
}

/** Shape of the 409 PARTS_DISPOSITION_REQUIRED payload. */
export interface GatePart {
    id: string;
    short_code: string;
    description: string;
    unit_cost: number;
}

export type PartDispositionKind = "still_with_me" | "returning_to_office" | "left_on_site";

export interface PartDispositionAnswer {
    partId: string;
    disposition: PartDispositionKind;
    note?: string;
}

const STATE_LABEL: Record<string, string> = {
    received: "At the office",
    staged: "Staged for pickup",
    issued: "With you",
    installed: "Installed",
    returning: "Return to office",
    returning_to_vendor: "Going back to vendor",
};

// Solid chips (§V2 contrast doctrine) — one glance answers "did the kits
// make it onto the truck?"
const STATE_CHIP: Record<string, string> = {
    received: "bg-sky-700 text-white",
    staged: "bg-violet-600 text-white",
    issued: "bg-indigo-600 text-white",
    installed: "bg-emerald-700 text-white",
    returning: "bg-red-600 text-white",
    returning_to_vendor: "bg-red-600 text-white",
    checked_in: "bg-emerald-700 text-white",
};

export function PartsForJobCard({ jobId, jobStatus }: { jobId: string; jobStatus?: string | null }) {
    const qc = useQueryClient();
    // #796 — once the job is off the tech's hands this card is a RECEIPT, not a
    // control panel. It previously had no status gate at all, so a COMPLETED
    // job rendered an amber "N parts not staged" warning and a live gold
    // [I have it] button directly above the banner "Job sent to the office —
    // nothing to do here." The page told the tech two opposite things at once.
    // Gated on the step engine's own POST_WORK set so the card and the rail can
    // never disagree about what "done" means.
    const readOnly = isPostWorkStatus(jobStatus);
    const { data, isLoading } = useQuery({
        queryKey: ["field", "job-parts", jobId],
        queryFn: () =>
            api.get<{ parts: TrackedJobPart[]; pull_sheet_lines?: PullSheetLine[] }>(
                `/api/tracked-parts/for-job/${jobId}`,
            ),
        staleTime: 60_000,
    });
    const iHaveIt = useMutation({
        mutationFn: (partId: string) => api.post(`/api/tracked-parts/${partId}/i-have-it`),
        onSuccess: () => {
            toast.success("Got it — the part is in your custody now.");
            void qc.invalidateQueries({ queryKey: ["field", "job-parts", jobId] });
            void qc.invalidateQueries({ queryKey: ["field", "my-parts"] });
        },
        onError: () => toast.error("Could not record the hand-off. Try again."),
    });

    const parts = data?.parts ?? [];
    const pullLines = data?.pull_sheet_lines ?? [];
    // While loading we say nothing rather than briefly claiming "no parts".
    if (isLoading) return null;
    // #814 (doctrine rule 6 — states always speak) + #829: with the PO-lines
    // source in the payload, the empty state can now tell the truth precisely
    // — nothing ordered at all vs nothing tracked.
    if (parts.length === 0 && pullLines.length === 0) {
        return (
            <Card>
                <CardContent className="flex items-center gap-2 p-4" data-testid="parts-empty-state">
                    <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                        {/* #796 — "ask the office before you roll" is advice for a
                            job you're about to work, not one already sent in. */}
                        {readOnly
                            ? "No parts were ordered for this job."
                            : "No parts were ordered for this job. If the work needs one, ask the office before you roll."}
                    </p>
                </CardContent>
            </Card>
        );
    }
    const notStaged = parts.filter((p) => p.custody_state === "received").length;
    // #829: count physical objects, not custody rows — the audit found this
    // card saying "1 part" on a job whose PO had two real objects.
    const pullUnits = pullLines.reduce((n, l) => n + Math.max(1, Math.round(l.quantity)), 0);
    const totalObjects = parts.length + pullUnits;

    return (
        <Card>
            <CardContent className="space-y-2 p-4">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                    <Package className="h-4 w-4" />
                    {readOnly ? "Parts on this job" : "Parts for this job"}
                    <span className="text-xs font-normal text-muted-foreground" data-testid="parts-object-count">
                        {totalObjects} part{totalObjects === 1 ? "" : "s"}
                    </span>
                    {/* #796 — the amber "not staged" warning is a call to action.
                        On a job already sent to the office there is no action to
                        take, so it only contradicted the "nothing to do here"
                        banner underneath it. */}
                    {!readOnly && notStaged > 0 && (
                        <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-black">
                            {notStaged} part{notStaged === 1 ? "" : "s"} not staged
                        </span>
                    )}
                </div>
                {readOnly && (
                    <p className="text-xs text-muted-foreground" data-testid="parts-receipt-note">
                        Where these ended up when you finished. Nothing to do here — the office takes it from here.
                    </p>
                )}
                {parts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2 rounded-md border p-2">
                        <div className="min-w-0">
                            <div className="truncate text-sm font-medium">
                                <span className="font-mono">{p.short_code}</span> · {p.description}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                                {/* Techs never see pricing — custody state only. */}
                                <span
                                    className={`inline-block rounded-full px-2 py-0.5 font-semibold ${STATE_CHIP[p.custody_state] ?? "bg-muted text-foreground"}`}
                                >
                                    {STATE_LABEL[p.custody_state] ?? p.custody_state}
                                    {/* #830: a staged part with no location says so —
                                        the tech shouldn't discover the blank on site. */}
                                    {p.custody_state === "staged"
                                        ? ` · ${p.staged_note || "no location given — ask the office"}`
                                        : ""}
                                </span>
                            </div>
                        </div>
                        {/* #796 — no custody hand-off after the job left the tech. */}
                        {!readOnly && ["received", "staged", "checked_in"].includes(p.custody_state) ? (
                            <Button size="sm" onClick={() => iHaveIt.mutate(p.id)} disabled={iHaveIt.isPending}>
                                <Hand className="mr-1.5 h-4 w-4" /> I have it
                            </Button>
                        ) : p.custody_state === "issued" ? (
                            <span className="flex shrink-0 items-center gap-1 text-xs text-emerald-600">
                                <CheckCircle className="h-3.5 w-3.5" /> With you
                            </span>
                        ) : null}
                    </div>
                ))}
                {/* #829: the parts custody doesn't follow one-by-one — real
                    objects off the pull sheet. Grab these from the shelf; no
                    [I have it] because there's no custody record to transfer.
                    Techs never see pricing. */}
                {pullLines.map((l) => (
                    <div
                        key={l.po_line_id}
                        data-testid="pull-sheet-line"
                        className="rounded-md border border-dashed p-2"
                    >
                        <div className="truncate text-sm font-medium">
                            {l.part_name}
                            {l.quantity > 1 ? ` ×${Math.round(l.quantity)}` : ""}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                            <span
                                className="rounded-full border px-2 py-0.5 font-medium"
                                title="This part isn't tracked one-by-one — it's on the office's daily pull sheet. Grab it from the parts shelf."
                            >
                                from the pull sheet
                            </span>
                            <span>
                                {/* #796 — "grab it before you roll" is an instruction;
                                    it has no meaning on a job already turned in. */}
                                {readOnly
                                    ? l.is_fully_received
                                        ? "was at the office"
                                        : l.received_qty > 0
                                          ? `${Math.round(l.received_qty)} of ${Math.round(l.quantity)} in`
                                          : "was still on order"
                                    : l.is_fully_received
                                      ? "at the office — grab it before you roll"
                                      : l.received_qty > 0
                                        ? `${Math.round(l.received_qty)} of ${Math.round(l.quantity)} in`
                                        : "on order — not here yet"}
                            </span>
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}

const DISPOSITION_OPTIONS: Array<{ value: PartDispositionKind; label: string }> = [
    { value: "still_with_me", label: "Still with me — returning to this job" },
    { value: "returning_to_office", label: "Bringing back to the office" },
    { value: "left_on_site", label: "Left on site (explain)" },
];

export function PartsDispositionDialog({
    open,
    parts,
    busy,
    onCancel,
    onSubmit,
}: {
    open: boolean;
    parts: GatePart[];
    busy: boolean;
    onCancel: () => void;
    onSubmit: (answers: PartDispositionAnswer[]) => void;
}) {
    const [answers, setAnswers] = useState<Record<string, { disposition?: PartDispositionKind; note: string }>>({});

    const complete = parts.every((p) => {
        const a = answers[p.id];
        if (!a?.disposition) return false;
        if (a.disposition === "left_on_site" && !a.note.trim()) return false;
        return true;
    });

    return (
        <Dialog open={open} onClose={onCancel} title="Where are the parts?">
            <div className="space-y-4">
                <p className="text-sm text-foreground/80">
                    Tracked parts are in your custody on this job. Answer for each one before closing out.
                </p>
                {parts.map((p) => {
                    const a = answers[p.id] ?? { note: "" };
                    return (
                        <div key={p.id} className="space-y-2 rounded-md border p-3">
                            <div className="text-sm font-medium">
                                {/* Techs never see pricing — code + description only. */}
                                <span className="font-mono">{p.short_code}</span> · {p.description}
                            </div>
                            <div className="space-y-1.5">
                                {DISPOSITION_OPTIONS.map((opt) => (
                                    <label key={opt.value} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="radio"
                                            name={`disp-${p.id}`}
                                            checked={a.disposition === opt.value}
                                            onChange={() =>
                                                setAnswers((prev) => ({
                                                    ...prev,
                                                    [p.id]: { ...a, disposition: opt.value },
                                                }))
                                            }
                                        />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                            {a.disposition === "left_on_site" && (
                                <Textarea
                                    value={a.note}
                                    onChange={(e) =>
                                        setAnswers((prev) => ({
                                            ...prev,
                                            [p.id]: { ...a, note: e.target.value },
                                        }))
                                    }
                                    placeholder="Required: where on site, and why it stayed…"
                                    rows={2}
                                />
                            )}
                        </div>
                    );
                })}
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onCancel} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!complete || busy}
                        onClick={() =>
                            onSubmit(
                                parts.map((p) => ({
                                    partId: p.id,
                                    disposition: answers[p.id].disposition!,
                                    note: answers[p.id].note.trim() || undefined,
                                })),
                            )
                        }
                    >
                        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Confirm
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
